import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { PLANNERS } from './plannerConfig';
import { Section } from './PlannerFields';
import AiAssist from './AiAssist';
import { HexMark } from './PlannerHome';
import './planner.css';

const PAPER = {
  a4: { w: '210mm', h: '297mm', label: 'A4', page: '210mm 297mm' },
  a5: { w: '148mm', h: '210mm', label: 'A5', page: '148mm 210mm' },
};

const newEntry = () => ({ id: String(Date.now()), name: new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }), data: {}, createdAt: Date.now() });

export default function PlannerPage() {
  const { plannerId } = useParams();
  const planner = PLANNERS.find(p => p.id === plannerId);
  const storageKey = `rb_planner_${plannerId}`;

  const [store, setStore] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.entries && Object.keys(parsed.entries).length) return parsed;
      }
    } catch { /* fresh */ }
    const e = newEntry();
    return { entries: { [e.id]: e }, activeId: e.id, paper: 'a4' };
  });
  const [savedFlash, setSavedFlash] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      localStorage.setItem(storageKey, JSON.stringify(store));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [store, storageKey]);

  const entry = store.entries[store.activeId];
  const paper = PAPER[store.paper || 'a4'];

  const set = useCallback((key, value) => {
    setStore(prev => ({
      ...prev,
      entries: { ...prev.entries, [prev.activeId]: { ...prev.entries[prev.activeId], data: { ...prev.entries[prev.activeId].data, [key]: value } } }
    }));
  }, []);

  if (!planner) return <Navigate to="/planner" replace />;
  if (!entry) return null;

  const createEntry = () => {
    const e = newEntry();
    setStore(prev => ({ ...prev, entries: { ...prev.entries, [e.id]: e }, activeId: e.id }));
  };
  const duplicateEntry = () => {
    const e = { ...newEntry(), name: `${entry.name} (copy)`, data: JSON.parse(JSON.stringify(entry.data)) };
    setStore(prev => ({ ...prev, entries: { ...prev.entries, [e.id]: e }, activeId: e.id }));
  };
  const clearEntry = () => {
    if (!window.confirm('Clear all fields on this page?')) return;
    setStore(prev => ({ ...prev, entries: { ...prev.entries, [prev.activeId]: { ...prev.entries[prev.activeId], data: {} } } }));
  };
  const deleteEntry = () => {
    const ids = Object.keys(store.entries);
    if (ids.length === 1) { clearEntry(); return; }
    if (!window.confirm('Delete this saved page?')) return;
    setStore(prev => {
      const entries = { ...prev.entries };
      delete entries[prev.activeId];
      return { ...prev, entries, activeId: Object.keys(entries)[0] };
    });
  };
  const renameEntry = (name) => {
    setStore(prev => ({ ...prev, entries: { ...prev.entries, [prev.activeId]: { ...prev.entries[prev.activeId], name } } }));
  };

  const entryList = Object.values(store.entries).sort((a, b) => b.createdAt - a.createdAt);

  return (
    <div className="rb-planner">
      <style>{`@page { size: ${paper.page}; margin: 0; }`}</style>

      <div className="rb-toolbar rb-no-print" data-testid="planner-toolbar">
        <Link to="/planner" className="rb-btn" data-testid="back-to-planners">← Planners</Link>
        <span className="rb-serif" style={{ fontSize: '1.05rem', fontWeight: 600, marginRight: 'auto' }}>{planner.num} — {planner.title}</span>

        <select className="rb-select" value={store.activeId} data-testid="entry-select"
          onChange={e => setStore(prev => ({ ...prev, activeId: e.target.value }))}>
          {entryList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input className="rb-select" style={{ width: 120 }} value={entry.name} onChange={e => renameEntry(e.target.value)} data-testid="entry-name-input" />

        <button className="rb-btn" onClick={createEntry} data-testid="new-entry-btn">New</button>
        <button className="rb-btn" onClick={duplicateEntry} data-testid="duplicate-entry-btn">Duplicate</button>
        <button className="rb-btn" onClick={clearEntry} data-testid="clear-entry-btn">Clear</button>
        <button className="rb-btn" onClick={deleteEntry} data-testid="delete-entry-btn">Delete</button>

        <span style={{ width: 8 }} />
        {Object.entries(PAPER).map(([k, p]) => (
          <button key={k} className={`rb-btn ${store.paper === k ? 'rb-active' : ''}`}
            onClick={() => setStore(prev => ({ ...prev, paper: k }))} data-testid={`paper-${k}`}>{p.label}</button>
        ))}
        <button className="rb-btn rb-primary" onClick={() => window.print()} data-testid="print-pdf-btn">Print / Save PDF</button>
        <span className="rb-saved" data-testid="saved-indicator">{savedFlash ? 'Saved ✓' : ''}</span>
      </div>

      <div className="rb-sheet-wrap">
        <div className={`rb-sheet ${store.paper}`} style={{ '--sheet-w': paper.w, '--sheet-h': paper.h }} data-testid="planner-sheet">
          <div className="rb-sheet-header">
            <div>
              <div className="rb-wordmark">Ricky's Bees</div>
              <div style={{ fontSize: '6.5pt', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'var(--olive)', marginTop: '1mm' }}>Business Planner</div>
            </div>
            <div>
              <div className="rb-page-num">{planner.num} <HexMark size={10} /></div>
              <div className="rb-page-title rb-serif">{planner.title}</div>
            </div>
          </div>

          <div className="rb-sections">
            {planner.sections.map((section, i) => (
              <Section key={i} section={section} data={entry.data} set={set} />
            ))}
          </div>

          <div className="rb-sheet-footer">
            <span>Ricky's Bees</span>
            <span>Plan • Produce • Pack • Grow</span>
            <span>rickysbees.com.au</span>
          </div>
        </div>
      </div>

      <AiAssist planner={planner} entryData={entry.data} />
    </div>
  );
}
